import type { ChartViewMode } from '@/utils/chartRangeNormalizer';
import { apiRequest } from './apiClient';
import {
    formatExportFilenameDate,
    requestCsvExport,
    type ExportResult,
} from './exportRequest';

export type AccountsReportMetricKey = 'validated' | 'pending-validation' | 'delinquent' | 'unsettled-accounts';

export type AccountsReportStats = Record<AccountsReportMetricKey, number>;
export type AccountsReportChartSeries = Record<AccountsReportMetricKey, number[]>;

type AccountsReportStatsApiResponse = {
    validated_accounts: number;
    pending_accounts: number;
    delinquent_accounts: number;
    unsettled_accounts: number;
};

type AccountsReportChartApiResponse = {
    labels: string[];
    validated: number[];
    pending: number[];
    delinquent: number[];
    unsettled: number[];
    daily_averages: {
        validated: number;
        pending: number;
        delinquent: number;
        unsettled: number;
    };
};

export type AccountsReportChart = {
    labels: string[];
    series: AccountsReportChartSeries;
    dailyAverages: AccountsReportStats;
};

export const getAccountsReportStats = async (from: string, to: string): Promise<AccountsReportStats> => {
    const response = await apiRequest<AccountsReportStatsApiResponse>('/api/accounts/report/stats', 'GET', {
        params: {
            from,
            to,
        },
    });

    return {
        validated: response.validated_accounts,
        'pending-validation': response.pending_accounts,
        delinquent: response.delinquent_accounts,
        'unsettled-accounts': response.unsettled_accounts,
    };
};

export type PaymentsReportMetricKey = 'cleared' | 'upcoming' | 'low-balance' | 'canceled';

export type PaymentsReportStats = Record<PaymentsReportMetricKey, number>;

export type PaymentsReportChartSeries = Record<PaymentsReportMetricKey, number[]>;

export type PaymentsReportChart = {
    labels: string[];
    series: PaymentsReportChartSeries;
    dailyAverages: Record<PaymentsReportMetricKey, number>;
};

type PaymentsReportChartApiResponse = {
    labels: string[];
    cleared: number[];
    pending: number[];
    open: number[];
    low_balance: number[];
    cancel: number[];
    daily_averages: {
        cleared: number;
        pending: number;
        open: number;
        low_balance: number;
        cancel: number;
    };
};

type PaymentsReportStatsApiResponse = {
    total_cleared_transactions: number;
    total_cleared_amount: number;
    pending_transactions: number;
    pending_amount: number;
    open_transactions: number;
    open_amount: number;
    low_balance_transactions: number;
    low_balance_amount: number;
    cancel_transactions: number;
    cancel_amount: number;
};

export const getPaymentsReportStats = async (from: string, to: string): Promise<PaymentsReportStats> => {
    const response = await apiRequest<PaymentsReportStatsApiResponse>('/api/payments/report/stats', 'GET', {
        params: { from, to },
    });

    return {
        cleared: response.total_cleared_amount,
        upcoming: response.pending_amount + response.open_amount,
        'low-balance': response.low_balance_amount,
        canceled: response.cancel_amount,
    };
};

export const getPaymentsReportChart = async (
    from: string,
    to: string,
    viewMode: ChartViewMode,
): Promise<PaymentsReportChart> => {
    const response = await apiRequest<PaymentsReportChartApiResponse>('/api/payments/report/chart', 'GET', {
        params: { from, to, view: viewMode },
    });

    return {
        labels: response.labels,
        series: {
            cleared: response.cleared,
            upcoming: response.pending.map((v, i) => v + (response.open[i] ?? 0)),
            'low-balance': response.low_balance,
            canceled: response.cancel,
        },
        dailyAverages: {
            cleared: response.daily_averages.cleared,
            upcoming: response.daily_averages.pending + response.daily_averages.open,
            'low-balance': response.daily_averages.low_balance,
            canceled: response.daily_averages.cancel,
        },
    };
};

type PortfolioRiskApiResponse = {
    on_time_transactions: number;
    on_time_percentage: number;
    low_balance_transactions: number;
    low_balance_percentage: number;
    at_least_one_late_transactions: number;
    at_least_one_late_percentage: number;
    total: number;
};

export type PortfolioRiskData = {
    onTimeTransactions: number;
    onTimePercentage: number;
    lowBalanceTransactions: number;
    lowBalancePercentage: number;
    atLeastOneLateTransactions: number;
    atLeastOneLatePercentage: number;
    total: number;
};

export const getPortfolioRisk = async (from: string, to: string): Promise<PortfolioRiskData> => {
    const response = await apiRequest<PortfolioRiskApiResponse>('/api/payments/report/portfolio-risk', 'GET', {
        params: { from, to },
    });

    return {
        onTimeTransactions: response.on_time_transactions,
        onTimePercentage: response.on_time_percentage,
        lowBalanceTransactions: response.low_balance_transactions,
        lowBalancePercentage: response.low_balance_percentage,
        atLeastOneLateTransactions: response.at_least_one_late_transactions,
        atLeastOneLatePercentage: response.at_least_one_late_percentage,
        total: response.total,
    };
};

export const getAccountsReportChart = async (
    from: string,
    to: string,
    viewMode: ChartViewMode,
): Promise<AccountsReportChart> => {
    const response = await apiRequest<AccountsReportChartApiResponse>('/api/accounts/report/chart', 'GET', {
        params: {
            from,
            to,
            view: viewMode,
        },
    });

    return {
        labels: response.labels,
        series: {
            validated: response.validated,
            'pending-validation': response.pending,
            delinquent: response.delinquent,
            'unsettled-accounts': response.unsettled,
        },
        dailyAverages: {
            validated: response.daily_averages.validated,
            'pending-validation': response.daily_averages.pending,
            delinquent: response.daily_averages.delinquent,
            'unsettled-accounts': response.daily_averages.unsettled,
        },
    };
};

/* -------------------------------------------------------------------------- */
/* CSV export                                                                 */
/* -------------------------------------------------------------------------- */

export type {
    ExportQueuedResult as ExportReportQueuedResult,
    ExportSyncResult as ExportReportSyncResult,
} from './exportRequest';

export type ExportReportResult = ExportResult;

export type ExportPaymentsReportParams = {
    from: string;
    to: string;
    view: ChartViewMode;
    /** Selected stat card. Omit to export all four metrics plus a daily total column. */
    metric?: PaymentsReportMetricKey | null;
};

export type ExportPortfolioRiskReportParams = {
    from: string;
    to: string;
};

/** Matches backend ReportExportService::buildDownloadFilename(). */
export function buildReportsExportFilename(segment: 'Payments' | 'Portfolio-Risk', from?: string, to?: string): string {
    const fromBlank = !from || from.trim() === '';
    const toBlank = !to || to.trim() === '';
    if (fromBlank && toBlank) {
        return `FORTH_Creditor-Portal-Reports-${segment}-${formatExportFilenameDate(undefined)}_all.csv`;
    }

    return `FORTH_Creditor-Portal-Reports-${segment}-${formatExportFilenameDate(from)}-${formatExportFilenameDate(to)}.csv`;
}

/**
 * POST /api/payments/report/export — the payment report over the **full** selected range.
 *
 * Deliberately takes the raw date-range-picker values. The chart request narrows the range via
 * `normalizePaymentsChartRange()` so it never renders more than 20 bars; the export must not.
 */
export const exportPaymentsReport = async (
    params: ExportPaymentsReportParams,
): Promise<ExportReportResult> => {
    const body: Record<string, unknown> = {
        from: params.from,
        to: params.to,
        view: params.view,
    };
    if (params.metric) {
        body.metric = params.metric;
    }

    return requestCsvExport(
        '/api/payments/report/export',
        body,
        buildReportsExportFilename('Payments', params.from, params.to),
    );
};

/**
 * POST /api/payments/report/portfolio-risk/export — risk buckets for the selected range.
 */
export const exportPortfolioRiskReport = async (
    params: ExportPortfolioRiskReportParams,
): Promise<ExportReportResult> => requestCsvExport(
    '/api/payments/report/portfolio-risk/export',
    { from: params.from, to: params.to },
    buildReportsExportFilename('Portfolio-Risk', params.from, params.to),
);
